import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

// Compact numeric input with a calculator-style numpad popover.
//
// Renders as a single button showing the current value (e.g. "16.2" or
// the placeholder "—" when empty). Tapping it opens a small popover
// anchored on the button: a display row, a 3×4 numpad (1-9, ., 0, ⌫),
// and Clear / Done actions. Mirrors the magnification gesture used by
// ScoreScrollPicker / NumericPopoverPicker so the input pattern stays
// consistent across the CaddieGames modals.

interface Props {
  value: number | undefined;
  onChange: (next: number | undefined) => void;
  min?: number;
  max?: number;
  // Cap decimal places allowed during entry. Handicap indices are
  // universally one-decimal, so that's the default; callers can widen.
  maxDecimals?: number;
  // Pad the trigger display to this many decimal places, so an integer
  // value reads with the expected decimal slot (e.g. "19.0" not "19").
  // Falls back to natural rendering when undefined.
  displayDecimals?: number;
  // Max characters allowed in the entry buffer (including the decimal
  // point). Defaults to 5 — fine for handicaps ("54.0") but too short for
  // larger figures like a pot, where callers can widen it.
  maxLength?: number;
  placeholder?: string;
  ariaLabel?: string;
  className?: string;
}

const PAD_WIDTH = 200;
const PAD_HEIGHT = 260;

export default function NumpadPopover({
  value,
  onChange,
  min,
  max,
  maxDecimals = 1,
  displayDecimals,
  maxLength = 5,
  placeholder = "—",
  ariaLabel,
  className = "",
}: Props) {
  const [open, setOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const handleOpen = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) setAnchorRect(rect);
    setOpen(true);
  };

  const display =
    value == null
      ? placeholder
      : displayDecimals != null
        ? value.toFixed(displayDecimals)
        : formatValue(value);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`cg-numpad-trigger${className ? " " + className : ""}`}
        onClick={handleOpen}
        aria-label={ariaLabel}
      >
        {display}
      </button>
      {open && anchorRect && (
        <Pad
          anchorRect={anchorRect}
          initial={value}
          min={min}
          max={max}
          maxDecimals={maxDecimals}
          maxLength={maxLength}
          onCommit={(n) => {
            onChange(n);
            setOpen(false);
          }}
          onCancel={() => setOpen(false)}
        />
      )}
    </>
  );
}

// Trim trailing ".0" so a stored 16 displays as "16", not "16.0".
function formatValue(n: number): string {
  return Number.isInteger(n) ? String(n) : String(n);
}

// ─── Numpad popover ─────────────────────────────────────────────────────────

interface PadProps {
  anchorRect: DOMRect;
  initial: number | undefined;
  min: number | undefined;
  max: number | undefined;
  maxDecimals: number;
  maxLength: number;
  onCommit: (next: number | undefined) => void;
  onCancel: () => void;
}

function Pad({
  anchorRect,
  initial,
  min,
  max,
  maxDecimals,
  maxLength,
  onCommit,
  onCancel,
}: PadProps) {
  // Working buffer is a string so partial entries like "16." render
  // intuitively while typing without converting back-and-forth from a
  // number (which would lose the trailing dot).
  const [entry, setEntry] = useState<string>(
    initial != null ? String(initial) : "",
  );
  const entryRef = useRef(entry);
  entryRef.current = entry;

  const overlayRef = useRef<HTMLDivElement>(null);
  // Suppress the very-first mousedown so the tap that opened the pad
  // doesn't immediately fire the outside-click commit.
  const armedRef = useRef(false);

  useLayoutEffect(() => {
    const t = setTimeout(() => {
      armedRef.current = true;
    }, 0);
    return () => clearTimeout(t);
  }, []);

  const commit = useCallback(() => {
    const parsed = parseEntry(entryRef.current);
    onCommit(parsed == null ? undefined : clamp(parsed, min, max));
  }, [onCommit, min, max]);

  // Outside click / Enter → commit. Escape → cancel.
  useEffect(() => {
    const onPointer = (e: MouseEvent | TouchEvent) => {
      if (!armedRef.current) return;
      const target = e.target as Node;
      if (overlayRef.current && overlayRef.current.contains(target)) return;
      commit();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
      else if (e.key === "Enter") commit();
      else if (e.key === "Backspace") press("BACK");
      else if (e.key === ".") press(".");
      else if (/^\d$/.test(e.key)) press(e.key);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("touchstart", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("touchstart", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [commit, onCancel]);

  const press = (key: string) => {
    setEntry((cur) => {
      if (key === "BACK") return cur.slice(0, -1);
      if (key === ".") {
        if (cur.includes(".")) return cur;
        if (cur === "") return "0.";
        return cur + ".";
      }
      // digit
      if (cur.includes(".")) {
        const decimals = cur.split(".")[1].length;
        if (decimals >= maxDecimals) return cur;
      }
      // Total length cap keeps the display row tidy.
      if (cur.length >= maxLength) return cur;
      // Strip a leading zero so "07" doesn't appear; but allow "0."
      if (cur === "0" && key !== ".") return key;
      return cur + key;
    });
  };

  // Position below the trigger by default; flip above if it'd overflow.
  const cx = anchorRect.left + anchorRect.width / 2;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let left = cx - PAD_WIDTH / 2;
  let top = anchorRect.bottom + 6;
  if (left < 4) left = 4;
  if (left + PAD_WIDTH > vw - 4) left = vw - 4 - PAD_WIDTH;
  if (top + PAD_HEIGHT > vh - 4) top = anchorRect.top - PAD_HEIGHT - 6;
  if (top < 4) top = 4;

  return (
    <div className="cg-numpad-backdrop" aria-hidden>
      <div
        ref={overlayRef}
        className="cg-numpad-pop"
        role="dialog"
        aria-label="Number pad"
        style={{ left, top, width: PAD_WIDTH }}
      >
        <div className="cg-numpad-display">{entry || "0"}</div>
        <div className="cg-numpad-grid">
          {["1", "2", "3", "4", "5", "6", "7", "8", "9", ".", "0", "BACK"].map(
            (k) => (
              <button
                type="button"
                key={k}
                className={`cg-numpad-key${
                  k === "BACK" ? " cg-numpad-key-back" : ""
                }`}
                onClick={() => press(k)}
              >
                {k === "BACK" ? "⌫" : k}
              </button>
            ),
          )}
        </div>
        <div className="cg-numpad-actions">
          <button
            type="button"
            className="cg-btn cg-btn-ghost cg-numpad-clear"
            onClick={() => setEntry("")}
          >
            Clear
          </button>
          <button
            type="button"
            className="cg-btn cg-btn-primary cg-numpad-done"
            onClick={commit}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

function parseEntry(s: string): number | null {
  if (!s || s === "." || s === "0.") return null;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

function clamp(n: number, min?: number, max?: number): number {
  let v = n;
  if (min != null && v < min) v = min;
  if (max != null && v > max) v = max;
  return v;
}
